<template>
  <div :id="htmlId"></div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import ymaps from 'ymaps';

import { getBaseDomain } from '@/requestManager';

// Объявляем класс панорамы.
function PanoramaTemplate(angularBBox, position, tileSize, tileLevels) {
  // Вызываем конструктор родительского класса.
  window.ymapsInstance.panorama.Base.call(this);

  this._angularBBox = angularBBox;
  this._position = position;
  this._tileSize = tileSize;
  this._tileLevels = tileLevels;
}

const baseSettings = {
  angularBBox: [
    1.5707963267948966,
    6.283185307179586,
    -1.5707963267948966,
    0
  ],
  position: [
    0,
    0,
    0
  ],
  tileSize: [
    512,
    512
  ],
  tileLevels: [
    {},
    {}
  ]
};

export default {
  name: 'Panorama',

  data: () => ({
    panoramaClass: null,
    panorama: null,
    player: null,
  }),
  props: {
    msg: String,
    panoramaId: Number,
    panoramaFilename: String,
  },
  computed: {
    settings() {
      const currentPanoramaDirectory = `${ getBaseDomain() }/img/panorama/${ this.panoramaId }`;

      baseSettings.tileLevels = [
        {
          // URL тайлов для высокого уровня детализации.
          getTileUrl: (x, y) => `${ currentPanoramaDirectory }/hq/${ x }-${ y }.jpg`,
          // Размер изображения для высокого уровня детализации.
          getImageSize: () => [7168, 3584],
        },
        {
          // URL тайлов для низкого уровня детализации.
          getTileUrl: () => `${ currentPanoramaDirectory }/lq/${this.panoramaFilename}`,
          // Размер изображения для низкого уровня детализации.
          getImageSize: () => [512, 256],
        },
      ];

      return baseSettings;
    },
    htmlId() {
      return `js-player-${ this.uid }`;
    },
    windowPlayerId() {
      return `panorama-player-${ this.uid }`;
    },
    uid() {
      return getCurrentInstance().uid;
    },
  },
  watch: {
    panoramaId() {
      this.changePanorama();
    },
  },

  async mounted() {
    await this.$nextTick();
    await this.initPackage();
    await this.initPanorama();
  },

  methods: {
    async initPackage() {
      window.ymapsInstance = await ymaps.load();

      // Сначала описываем уровни масштабирования панорамного изображения.
      // Для этого заводим класс, реализующий интерфейс IPanoramaTileLevel.
      // Параметрами конструктора будут шаблон URL тайлов и размер уровня.
      function TileLevel (urlTemplate, imageSize) {
        this._urlTemplate = urlTemplate;
        this._imageSize = imageSize;
      }

      window.ymapsInstance.util.defineClass(TileLevel, {
        getTileUrl: function (x, y) {
          // Определяем URL тайла для переданных индексов.
          return this._urlTemplate.replace('%c', y + '-' + x);
        },

        getImageSize: function () {
          return this._imageSize;
        }
      });

      window.ymapsInstance.util.defineClass(PanoramaTemplate, window.ymapsInstance.panorama.Base, {
        // Переопределяем метод getMarkers.
        getAngularBBox: function () {
          return this._angularBBox;
        },
        getPosition: function () {
          return this._position;
        },
        getTileSize: function () {
          return this._tileSize;
        },
        getTileLevels: function () {
          return this._tileLevels;
        },
        getCoordSystem: function () {
          return window.ymapsInstance.coordSystem.cartesian;
        }
      });
    },
    async initPanorama() {
      this.updatePanoramaSettingsTileLevels();

      // Для начала проверим, поддерживает ли плеер браузер пользователя.
      if (!window.ymapsInstance.panorama.isSupported()) {
        // Если нет, то ничего не будем делать.
        return;
      }

      // const settings = this.settings.tileLevels;
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const $this = this;

      // Теперь описываем панораму.
      function Panorama () {
        window.ymapsInstance.panorama.Base.call(this);
        // Наша панорама будет содержать два уровня масштабирования
        // панорамного изображения: низкого и высокого качества.
        this._tileLevels = $this.settings.tileLevels;
      }

      this.panoramaClass = Panorama;

      // Наследуем класс панорамы от window.ymapsInstance.panorama.Base, который частично
      // реализует IPanoramaTileLevel за нас.
      window.ymapsInstance.util.defineClass(this.panoramaClass, window.ymapsInstance.panorama.Base, {
        getPosition: function () {
          // Панорама будет располагаться в начале координат...
          return [0, 0, 0];
        },

        getCoordSystem: function () {
          // ...декартовой системы.
          return window.ymapsInstance.coordSystem.cartesian;
        },

        getAngularBBox: function () {
          // Область, которую занимает панорама на панорамной сфере.
          // В нашем случае это будет вся сфера.
          return [
            0.5 * Math.PI,
            2 * Math.PI,
            -0.5 * Math.PI,
            0
          ];
        },

        getTileSize: function () {
          // Размер тайлов, на которые нарезано изображение.
          return [512, 512];
        },

        getTileLevels: function () {
          return this._tileLevels;
        }
      });

      this.panorama = new this.panoramaClass();
      // инициализируем плеер, который и отображает панораму
      // объявляем вне контекста this - потому что с контекстом долго переключаются панорамы
      window[this.windowPlayerId] = new window.ymapsInstance.panorama.Player(this.htmlId, this.panorama);
    },
    updatePanoramaSettingsTileLevels() {
      const currentPanoramaDirectory = `${ getBaseDomain() }/img/panorama/${ this.panoramaId }`;

      this.settings.tileLevels = [
        {
          // URL тайлов для высокого уровня детализации.
          getTileUrl: (x, y) => `${ currentPanoramaDirectory }/hq/${ x }-${ y }.jpg`,
          // Размер изображения для высокого уровня детализации.
          getImageSize: () => [7168, 3584],
        },
        {
          // URL тайлов для низкого уровня детализации.
          getTileUrl: () => `${ currentPanoramaDirectory }/lq/${this.panoramaFilename}`,
          // Размер изображения для низкого уровня детализации.
          getImageSize: () => [512, 256],
        },
      ];
    },

    changePanorama() {
      this.updatePanoramaSettingsTileLevels();
      this.panorama = new this.panoramaClass();
      window[this.windowPlayerId].setPanorama(this.panorama);
    },
  },
}
</script>

<style lang="scss" scoped>
/deep/ ymaps {
  box-sizing: content-box;
}
</style>
